import React from 'react';
import { Alert, Button } from 'antd';
import { ExclamationCircleOutlined, EyeOutlined } from '@ant-design/icons';
import type { AntiCheatReport } from '../services/antiCheat';

interface AntiCheatWarningProps {
    report: AntiCheatReport;
    onViewDetails: () => void;
    onDismiss?: () => void;
}

const AntiCheatWarning: React.FC<AntiCheatWarningProps> = ({
    report,
    onViewDetails,
    onDismiss
}) => {
    const getRiskLevelProps = () => {
        switch (report.risk_level) {
            case 'critical':
                return {
                    type: 'error' as const,
                    message: 'Critical Anti-Cheat Violations Detected',
                    description: `${report.flagged_events_count} violations detected. This assessment result requires immediate review.`
                };
            case 'high':
                return {
                    type: 'warning' as const,
                    message: 'High Risk Anti-Cheat Activity',
                    description: `${report.flagged_events_count} suspicious activities detected during the assessment.`
                };
            case 'medium':
                return {
                    type: 'info' as const,
                    message: 'Moderate Anti-Cheat Activity',
                    description: `${report.flagged_events_count} activities flagged. Assessment integrity appears acceptable.`
                };
            case 'low':
            default:
                return null; // Don't show warning for low risk
        }
    };

    const alertProps = getRiskLevelProps();

    // Don't render anything for low risk or no violations
    if (!alertProps || !report.has_violations) {
        return null;
    }

    return (
        <Alert
            {...alertProps}
            showIcon
            icon={<ExclamationCircleOutlined />}
            style={{
                marginBottom: 0,
                padding: '12px 16px', // Reduced padding to decrease height
                minHeight: 'auto', // Allow natural height
                border: `1px solid ${report.risk_level === 'critical'
                    ? '#ff4d4f'
                    : report.risk_level === 'high'
                        ? '#faad14'
                        : '#1890ff'
                    }`
            }}
            action={
                <Button
                    size="small"
                    type="primary"
                    icon={<EyeOutlined />}
                    onClick={onViewDetails}
                    style={{
                        backgroundColor: report.risk_level === 'critical' ? '#ff4d4f' : undefined
                    }}
                >
                    View Details
                </Button>
            }
            closable={!!onDismiss}
            onClose={onDismiss}
        />
    );
};

export default AntiCheatWarning;